import { MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { GAMMA_DESK_ID } from '@/api/chat/constants';
import { useCancelInvestment } from '@/api/investing/mutations';
import { canCancelInvestment, deriveInvestorStatus } from '@/api/investing/state-machine';
import { Investment } from '@/api/investing/types';
import { getInvestWizardPath } from '@/api/investing/wizard-path';
import { MyInvestmentBoardFundDto } from '@/api/investing-board/types';
import MoreVerticalIcon from '@/assets/svg/ui-elements/dots-vertical.svg';
import HistoryIcon from '@/assets/svg/ui-elements/history.svg';
import MessageSquareIcon from '@/assets/svg/ui-elements/message-square.svg';
import XIcon from '@/assets/svg/ui-elements/x.svg';
import { useStartTypedThread } from '@/pages/communications-v2/hooks/use-start-typed-thread';
import {
  Button,
  IconButton,
  Menu,
  type MenuItem,
  Modal,
  Spinner,
  useToast,
} from '@/shared/design-system';

import { InvestmentStageTimeline } from '../investment-stage-timeline';
import { ActionsRow, CancelPendingState, MenuSlot } from './pending-asset-actions.styled';

interface PendingAssetActionsProps {
  investment: Investment;
  /** `slug`/`name` are null when the board's soft-fail fund lookup could not resolve them. */
  fund?: MyInvestmentBoardFundDto;
  mobile?: boolean;
}

export const PendingAssetActions = ({
  investment,
  fund,
  mobile = false,
}: PendingAssetActionsProps) => {
  const navigate = useNavigate();
  const { add: addToast } = useToast();
  const { mutate: cancelInvestment, isPending: isCancelling } = useCancelInvestment();
  const { start: startTypedThread, isPending: isContactingFund } = useStartTypedThread();
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isProgressOpen, setIsProgressOpen] = useState(false);

  const status = deriveInvestorStatus(investment);
  const canCancel = canCancelInvestment(investment);
  const isWireInTransit = status === 'wire in transit';

  const stop = (event: MouseEvent) => event.stopPropagation();

  const handleResume = (event: MouseEvent) => {
    stop(event);
    if (!fund?.slug) return;
    navigate(getInvestWizardPath(fund.slug, investment.id));
  };

  const handleWithdrawConfirm = () => {
    cancelInvestment(
      { investmentId: investment.id, realFundId: investment.fundId },
      {
        onSuccess: () => {
          setIsCancelOpen(false);
          addToast({
            title: 'Investment withdrawn',
            description: fund?.name
              ? `Your investment in ${fund.name} was withdrawn.`
              : 'Your investment was withdrawn.',
            data: { status: 'success' },
          });
        },
      },
    );
  };

  const handleContactFund = () => {
    const fundId = fund?.id ?? investment.fundId;
    startTypedThread({
      recipientType: 'GammaDesk',
      recipientId: GAMMA_DESK_ID,
      name: fund?.name ? `${fund.name} Fund` : 'Investment Inquiry',
      type: 'Inquiry',
      ...(fundId && { about: { type: 'Fund', id: fundId } }),
    });
  };

  const getPrimaryLabel = () => {
    if (canCancel) return 'Continue';
    if (isWireInTransit) return 'Payment details';
    return null;
  };

  const primaryLabel = getPrimaryLabel();
  const subject = fund?.name ?? 'this investment';

  const withdrawItems: MenuItem[] = [
    { type: 'separator', id: 'withdraw-separator' },
    {
      id: 'withdraw',
      label: 'Withdraw',
      icon: <XIcon />,
      tone: 'destructive',
      onSelect: () => setIsCancelOpen(true),
    },
  ];

  const menuItems: MenuItem[] = [
    {
      id: 'progress',
      label: 'View progress',
      icon: <HistoryIcon />,
      onSelect: () => setIsProgressOpen(true),
    },
    {
      id: 'contact-fund',
      label: 'Contact fund',
      icon: <MessageSquareIcon />,
      disabled: isContactingFund,
      onSelect: handleContactFund,
    },
    ...(canCancel ? withdrawItems : []),
  ];

  return (
    <ActionsRow $mobile={mobile} onClick={stop}>
      {primaryLabel && fund?.slug && (
        <Button
          color="secondary"
          variant="subtle"
          size="md"
          fullWidth={mobile}
          onClick={handleResume}
        >
          {primaryLabel}
        </Button>
      )}

      <MenuSlot>
        <Menu
          ariaLabel={`Actions for ${subject}`}
          items={menuItems}
          trigger={
            <IconButton
              variant="outline"
              color="tertiary"
              size={mobile ? 'md' : 'sm'}
              aria-label={`More actions for ${subject}`}
            >
              <MoreVerticalIcon />
            </IconButton>
          }
        />
      </MenuSlot>

      <Modal
        open={isProgressOpen}
        onOpenChange={setIsProgressOpen}
        variant="default"
        title="Investment progress"
        showClose
      >
        <InvestmentStageTimeline investment={investment} />
      </Modal>

      <Modal
        open={isCancelOpen}
        onOpenChange={setIsCancelOpen}
        variant="center"
        title="Withdraw from this investment?"
        description="This tells the fund you're not interested. You can start a new subscription any time."
        primaryAction={{
          label: 'Withdraw',
          color: 'destructive',
          onClick: handleWithdrawConfirm,
        }}
        secondaryAction={{ label: 'Keep investment', onClick: () => setIsCancelOpen(false) }}
      >
        <CancelPendingState $busy={isCancelling}>{isCancelling && <Spinner />}</CancelPendingState>
      </Modal>
    </ActionsRow>
  );
};

export default PendingAssetActions;
